import { CONNECTOR } from './constants';

export interface Point {
    x: number;
    y: number;
  }

  /** Minimal shape needed to locate a connector in world space. */
  export interface ConnectorPosition {
    xPosition: number;
    yPosition: number;
  }

  /** World-space center point of a connector, given its top-left position. */
  export function connectorCenter(connector: ConnectorPosition): Point {
    return {
      x: connector.xPosition + CONNECTOR.CENTER_X,
      y: connector.yPosition + CONNECTOR.CENTER_Y
    };
  }

  export class GeometryUtils {
    static distance(p1: Point, p2: Point): number {
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      return Math.sqrt(dx * dx + dy * dy);
    }
  
    static distanceToLine(
      point: Point, 
      lineStart: Point, 
      lineEnd: Point
    ): number {
      const dx = lineEnd.x - lineStart.x;
      const dy = lineEnd.y - lineStart.y;
      const length = Math.sqrt(dx * dx + dy * dy);
      
      if (length === 0) {
        return this.distance(point, lineStart);
      }
      
      const t = Math.max(0, Math.min(1, 
        ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / (length * length)
      ));
      
      const projX = lineStart.x + t * dx;
      const projY = lineStart.y + t * dy;
      
      return this.distance(point, { x: projX, y: projY });
    }
  
    static isPointInCircle(
      point: Point, 
      center: Point, 
      radius: number
    ): boolean {
      return this.distance(point, center) <= radius;
    }
  
    static isPointInRectangle(
      point: Point,
      rect: { x: number; y: number; width: number; height: number }
    ): boolean {
      return (
        point.x >= rect.x &&
        point.x <= rect.x + rect.width &&
        point.y >= rect.y &&
        point.y <= rect.y + rect.height
      );
    }
  
    static lerp(start: number, end: number, factor: number): number {
      return start + (end - start) * factor;
    }
  
    static lerpPoint(start: Point, end: Point, factor: number): Point {
      return {
        x: this.lerp(start.x, end.x, factor),
        y: this.lerp(start.y, end.y, factor)
      };
    }
  
    static normalizeAngle(angle: number): number {
      while (angle < 0) angle += 2 * Math.PI;
      while (angle >= 2 * Math.PI) angle -= 2 * Math.PI;
      return angle;
    }
  
    static angleBetweenPoints(p1: Point, p2: Point): number {
      return Math.atan2(p2.y - p1.y, p2.x - p1.x);
    }
  }